import { ENTRY_ATTACHMENT_EXTENSIONS, ENTRY_ATTACHMENT_IMAGE_EXTENSIONS } from 'common/constants/file.upload.types';
import {
  ENTRY_ATTACHMENT_MAX_BYTES,
  ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION,
} from 'common/constants/file.upload.limits';
import { uploadEntryAttachment } from '@/api/resources/entryAttachment.api';
import { useToast } from '@/composables/useToast';
import { createImageThumbnail } from '@/utils/image.utils';
import type { IEntryAttachmentTarget } from 'common/enum/entry.attachment.enum';
import { useLocale } from '@/composables/useLocale';
import { formatFileSize, getAttachmentExtension } from 'common/utils/file.utils';
import { computed, onScopeDispose, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export enum EPendingAttachmentStatus {
  Pending = 'pending',
  Uploading = 'uploading',
  Uploaded = 'uploaded',
}

export interface IPendingAttachment {
  id: number;
  file: File;
  imageUrl: string | null;
  status: EPendingAttachmentStatus;
}

export function useAttachmentUploads() {
  const { persistentErrorToast, loadingToast } = useToast();
  const { t } = useI18n();
  const { formattingLocale } = useLocale();
  const pending = ref<IPendingAttachment[]>([]);
  const uploading = ref(false);
  const refreshFailed = ref(false);
  let nextId = 0;

  const remove = (id: number): void => {
    const item = pending.value.find((entry) => entry.id === id);
    if (!item || item.status === EPendingAttachmentStatus.Uploading) return;
    if (item.imageUrl) URL.revokeObjectURL(item.imageUrl);
    pending.value = pending.value.filter((entry) => entry.id !== id);
  };

  const add = (files: File[]): void => {
    // the cap counts files still waiting, so a second selection cannot push a draft past it
    const room = ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION - pending.value.length;
    if (files.length > room) {
      persistentErrorToast(t('entryAttachment.tooMany', { count: ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION }));
    }
    for (const file of files.slice(0, Math.max(room, 0))) {
      const extension = getAttachmentExtension(file.name);
      if (!ENTRY_ATTACHMENT_EXTENSIONS.includes(extension) || !file.size) {
        persistentErrorToast(t('entryAttachment.invalidType', { name: file.name }));
        continue;
      }
      if (file.size > ENTRY_ATTACHMENT_MAX_BYTES) {
        persistentErrorToast(
          t('entryAttachment.tooLarge', {
            name: file.name,
            size: formatFileSize(ENTRY_ATTACHMENT_MAX_BYTES, formattingLocale.value),
          }),
        );
        continue;
      }
      pending.value.push({
        id: ++nextId,
        file,
        status: EPendingAttachmentStatus.Pending,
        imageUrl: ENTRY_ATTACHMENT_IMAGE_EXTENSIONS.includes(extension) ? URL.createObjectURL(file) : null,
      });
    }
  };

  const uploadOne = async (item: IPendingAttachment, target: IEntryAttachmentTarget): Promise<boolean> => {
    item.status = EPendingAttachmentStatus.Uploading;
    try {
      // A GIF thumbnail would freeze the animation on the card.
      const isGif = getAttachmentExtension(item.file.name) === '.gif';
      await uploadEntryAttachment(target, item.file, isGif ? null : await createImageThumbnail(item.file));
      item.status = EPendingAttachmentStatus.Uploaded;
      return true;
    } catch {
      item.status = EPendingAttachmentStatus.Pending;
      remove(item.id);
      persistentErrorToast(t('entryAttachment.uploadFailed', { name: item.file.name }));
      return false;
    }
  };

  // A batch of only images says so; any document makes it a batch of files.
  const batchMessage = (key: 'uploading' | 'uploaded', items: IPendingAttachment[]): string =>
    t(`entryAttachment.${key}${items.every((item) => item.imageUrl) ? 'Images' : 'Files'}`, { count: items.length });

  const flush = async (target: IEntryAttachmentTarget, refresh?: () => Promise<void>): Promise<boolean> => {
    if (uploading.value) return false;
    uploading.value = true;
    let complete = true;
    const hadUploaded = pending.value.some((item) => item.status === EPendingAttachmentStatus.Uploaded);
    const queued = pending.value.filter((item) => item.status !== EPendingAttachmentStatus.Uploaded);
    const progress = queued.length ? loadingToast(batchMessage('uploading', queued)) : null;
    const uploaded: IPendingAttachment[] = [];
    try {
      // Preserve selection order when persisted attachments replace the local cards.
      for (const item of queued) {
        if (!pending.value.some((current) => current.id === item.id)) continue;
        if (await uploadOne(item, target)) uploaded.push(item);
        else complete = false;
      }
      if (hadUploaded || uploaded.length) await refresh?.();
      for (const item of [...pending.value]) if (item.status === EPendingAttachmentStatus.Uploaded) remove(item.id);
      refreshFailed.value = false;
      return complete;
    } catch {
      refreshFailed.value = true;
      persistentErrorToast(t('entryAttachment.loadFailed'));
      return false;
    } finally {
      if (uploaded.length) progress?.succeed(batchMessage('uploaded', uploaded));
      else progress?.dismiss();
      uploading.value = false;
    }
  };

  const clear = (): void => {
    for (const item of [...pending.value]) remove(item.id);
    refreshFailed.value = false;
  };
  onScopeDispose(() => {
    for (const item of pending.value) if (item.imageUrl) URL.revokeObjectURL(item.imageUrl);
  });
  return {
    pending,
    uploading,
    refreshFailed,
    hasPending: computed(() => pending.value.length > 0),
    add,
    remove,
    flush,
    clear,
  };
}
